import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Badge, Spinner, Alert, Table
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useTrainingAuth } from '../../context/TrainingAuthContext';
import TrainingNavbar from '../../components/TrainingNavbar';
import trainingService from '../../services/trainingService';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',             bg: 'secondary', text: undefined },
  submitted: { label: 'Pending Review',    bg: 'primary',   text: undefined },
  sent_back: { label: 'Corrections Needed', bg: 'danger',   text: undefined },
  approved:  { label: 'Approved',          bg: 'success',   text: undefined },
};

const COORDINATOR_ROLES = ['training_coordinator', 'training_document_controller'];
const REVIEWER_ROLES = ['training_reviewer', 'training_admin'];

const ViewTrainingSubmission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { trainingUser } = useTrainingAuth();

  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');
  const [generateError, setGenerateError] = useState('');

  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [downloading, setDownloading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  const isCoordinator = COORDINATOR_ROLES.includes(trainingUser?.role);
  const isReviewer = REVIEWER_ROLES.includes(trainingUser?.role);

  const loadSubmission = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await trainingService.getSubmission(id);
      if (data.success) setSubmission(data.submission);
      else setError(data.message || 'Failed to load submission');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadSubmission(); }, [loadSubmission]);

  useEffect(() => {
    let objectUrl = null;
    if (submission?.traineePhoto) {
      trainingService.getTraineePhoto(id)
        .then(res => {
          objectUrl = URL.createObjectURL(res.data);
          setPhotoUrl(objectUrl);
        })
        .catch(() => {});
    }
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [submission?.traineePhoto, id]);

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    setSubmitMsg('');
    try {
      const result = await trainingService.submitForReview(id);
      if (result.success) {
        setSubmitMsg('Submitted for review successfully.');
        await loadSubmission();
      } else {
        setError(result.message || 'Failed to submit');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to submit for review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGeneratePdf = async () => {
    setGenerating(true);
    setGenerateMsg('');
    setGenerateError('');
    try {
      const result = await trainingService.generatePdf(id);
      if (result.success) {
        setGenerateMsg(result.message || 'PDF generated.');
        await loadSubmission();
      } else {
        setGenerateError(result.message || 'PDF generation failed');
      }
    } catch (e) {
      setGenerateError(e.response?.data?.message || 'PDF generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await trainingService.downloadPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `training_certificate_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewError('');
    try {
      const response = await trainingService.previewPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // revoke after a short delay so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setPreviewError('Preview generation failed. Please try again.');
      console.error('Preview failed:', e);
    } finally {
      setPreviewing(false);
    }
  };

  const dashboardPath = isCoordinator ? '/training/coordinator' : '/training/reviewer';

  if (loading) {
    return (
      <>
        <TrainingNavbar />
        <div className="text-center py-5"><Spinner animation="border" variant="success" /></div>
      </>
    );
  }

  if (error && !submission) {
    return (
      <>
        <TrainingNavbar />
        <Container className="py-4">
          <Alert variant="danger">{error}</Alert>
          <Button variant="outline-secondary" onClick={() => navigate(dashboardPath)}>Back</Button>
        </Container>
      </>
    );
  }

  if (!submission) return null;

  const hasPdf = submission.hasPdf;
  const statusCfg = STATUS_CONFIG[submission.current_status] || { label: submission.current_status || 'Legacy', bg: 'secondary' };
  const verification = submission.verification;

  const fieldValues = {};
  (submission.fields || []).forEach(f => { fieldValues[f.fieldName] = f.value; });

  const getVal = (submissionCol, ...fieldNames) => {
    if (submissionCol) return submissionCol;
    for (const name of fieldNames) { if (fieldValues[name]) return fieldValues[name]; }
    return null;
  };

  const summaryRefNo = getVal(submission.refNo, 'ref_no', 'reference_no', 'refNo');
  const summaryCertNo = getVal(submission.certNo, 'cert_no', 'certificate_no', 'certNo', 'certificate_number');
  const summaryIssueDate = getVal(submission.issueDate, 'issDate', 'issue_date', 'date_of_issue', 'issueDate');
  const summaryValidUntil = getVal(submission.validUntil, 'valDt', 'valid_until', 'valid_upto', 'validUntil');

  const sectionMap = {};
  (submission.fields || []).forEach(f => {
    const sec = f.sectionName || 'General';
    if (!sectionMap[sec]) sectionMap[sec] = [];
    sectionMap[sec].push(f);
  });

  const canSubmitForReview = isCoordinator &&
    (submission.current_status === 'draft' || submission.current_status === 'sent_back');

  return (
    <>
      <TrainingNavbar />
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4 align-items-start">
          <Col>
            <Button variant="link" className="ps-0 text-muted" onClick={() => navigate(dashboardPath)}>
              <i className="fas fa-arrow-left me-1"></i> Back to Dashboard
            </Button>
            <div className="d-flex align-items-center gap-2 mt-1">
              <h3 className="mb-0">Training Certificate Detail</h3>
              <Badge bg={statusCfg.bg}>{statusCfg.label}</Badge>
            </div>
          </Col>
          <Col xs="auto" className="d-flex gap-2 flex-wrap justify-content-end">
            {canSubmitForReview && (
              <Button variant="success" onClick={handleSubmitForReview} disabled={submitting}>
                {submitting
                  ? <><Spinner size="sm" className="me-2" />Submitting...</>
                  : <><i className="fas fa-paper-plane me-2"></i>Submit for Review</>}
              </Button>
            )}
            {isCoordinator && (submission.current_status === 'draft' || submission.current_status === 'sent_back') && (
              <Button variant="outline-primary" onClick={() => navigate(`/training/submissions/${id}/edit`)}>
                <i className="fas fa-edit me-2"></i>Edit
              </Button>
            )}
            {isReviewer && hasPdf && (
              <Button variant="outline-success" onClick={handleGeneratePdf} disabled={generating}>
                {generating
                  ? <><Spinner size="sm" className="me-2" />Regenerating…</>
                  : <><i className="fas fa-redo me-2"></i>Regenerate PDF</>}
              </Button>
            )}
            {hasPdf && (
              <Button variant="success" onClick={handleDownload} disabled={downloading}>
                {downloading
                  ? <><Spinner size="sm" className="me-2" />Downloading…</>
                  : <><i className="fas fa-download me-2"></i>Download PDF</>}
              </Button>
            )}
          </Col>
        </Row>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {submitMsg && <Alert variant="success" dismissible onClose={() => setSubmitMsg('')}>{submitMsg}</Alert>}
        {generateMsg && <Alert variant="success" dismissible onClose={() => setGenerateMsg('')}>{generateMsg}</Alert>}
        {generateError && <Alert variant="danger" dismissible onClose={() => setGenerateError('')}>{generateError}</Alert>}
        {previewError && <Alert variant="danger" dismissible onClose={() => setPreviewError('')}>{previewError}</Alert>}

        {/* Reviewer comments (sent-back) */}
        {submission.current_status === 'sent_back' && submission.review_comments && (
          <Alert variant="danger" className="d-flex gap-2">
            <i className="fas fa-exclamation-circle fa-lg mt-1"></i>
            <div>
              <strong>Corrections Required:</strong>
              <div>{submission.review_comments}</div>
              <small className="text-muted">
                Reviewed by: {submission.reviewerName || 'Reviewer'}
              </small>
            </div>
          </Alert>
        )}

        <Row>
          {/* Left: form data */}
          <Col lg={8}>
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-success text-white">
                <h6 className="mb-0"><i className="fas fa-info-circle me-2"></i>Certificate Summary</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <dl className="row mb-0">
                      <dt className="col-5 text-muted">Course</dt>
                      <dd className="col-7">{submission.courseName}</dd>
                      <dt className="col-5 text-muted">Course Code</dt>
                      <dd className="col-7">{submission.courseCode || '—'}</dd>
                      <dt className="col-5 text-muted">Cert Type</dt>
                      <dd className="col-7">
                        <Badge bg={submission.certType === 'ADNOC' ? 'primary' : 'info'}>
                          {submission.certType}
                        </Badge>
                      </dd>
                      <dt className="col-5 text-muted">Ref No</dt>
                      <dd className="col-7">{summaryRefNo || '—'}</dd>
                    </dl>
                  </Col>
                  <Col md={6}>
                    <dl className="row mb-0">
                      <dt className="col-5 text-muted">Cert No</dt>
                      <dd className="col-7">{summaryCertNo || '—'}</dd>
                      <dt className="col-5 text-muted">Issue Date</dt>
                      <dd className="col-7">{formatDate(summaryIssueDate)}</dd>
                      <dt className="col-5 text-muted">Valid Until</dt>
                      <dd className="col-7">{formatDate(summaryValidUntil)}</dd>
                      <dt className="col-5 text-muted">Submitted By</dt>
                      <dd className="col-7">{submission.submitterName}</dd>
                    </dl>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {Object.entries(sectionMap).map(([sectionName, sectionFields]) => (
              <Card className="shadow-sm mb-4" key={sectionName}>
                <Card.Header><h6 className="mb-0">{sectionName}</h6></Card.Header>
                <Card.Body className="p-0">
                  <Table className="mb-0" bordered>
                    <tbody>
                      {sectionFields.map(f => (
                        <tr key={f.id}>
                          <td className="text-muted fw-semibold" style={{ width: '40%' }}>{f.fieldLabel}</td>
                          <td>{f.value || <span className="text-muted">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            ))}

            {/* Status history */}
            {submission.statusHistory && submission.statusHistory.length > 0 && (
              <Card className="shadow-sm mb-4">
                <Card.Header><h6 className="mb-0"><i className="fas fa-history me-2"></i>Status History</h6></Card.Header>
                <Card.Body className="p-0">
                  <Table className="mb-0" size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>Status</th>
                        <th>By</th>
                        <th>Comments</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submission.statusHistory.map(h => (
                        <tr key={h.id}>
                          <td>
                            <Badge bg={(STATUS_CONFIG[h.current_status] || { bg: 'secondary' }).bg}
                              text={(STATUS_CONFIG[h.current_status] || {}).bg === 'warning' ? 'dark' : undefined}>
                              {(STATUS_CONFIG[h.current_status] || { label: h.current_status }).label}
                            </Badge>
                          </td>
                          <td><small>{h.changerName}</small></td>
                          <td><small className="text-muted">{h.comments || '—'}</small></td>
                          <td><small>{formatDate(h.changed_at)}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Right: PDF + QR + client */}
          <Col lg={4}>
            <Card className="shadow-sm mb-4">
              <Card.Header><h6 className="mb-0">PDF Certificate</h6></Card.Header>
              <Card.Body className="text-center">
                {hasPdf ? (
                  <>
                    <i className="fas fa-file-pdf fa-3x text-success mb-2 d-block"></i>
                    <Badge bg="success" className="mb-3">Certificate Ready</Badge>
                    <div className="d-grid">
                      <Button variant="success" onClick={handleDownload} disabled={downloading}>
                        {downloading
                          ? <><Spinner size="sm" className="me-2" />Downloading…</>
                          : <><i className="fas fa-download me-2"></i>Download PDF</>}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-pdf fa-3x text-muted mb-2 d-block"></i>
                    <Badge bg="warning" text="dark" className="mb-2">Not Generated</Badge>
                    <small className="text-muted d-block">
                      {isCoordinator
                        ? 'PDF will be generated after reviewer approval.'
                        : 'Approve this submission to generate the PDF.'}
                    </small>
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Certificate Preview — coordinator only, no signatures/seal/QR */}
            {isCoordinator && (
              <Card className="shadow-sm mb-4 border-primary">
                <Card.Header className="bg-primary bg-opacity-10">
                  <h6 className="mb-0 text-primary">
                    <i className="fas fa-search me-2"></i>Certificate Preview
                  </h6>
                </Card.Header>
                <Card.Body className="text-center">
                  <i className="fas fa-eye fa-2x text-primary mb-2 d-block"></i>
                  <p className="text-muted small mb-3">
                    Preview how the certificate will look. Signatures, company seal,
                    and QR code will appear blank — they are added only after reviewer approval.
                  </p>
                  <div className="d-grid">
                    <Button
                      variant="outline-primary"
                      onClick={handlePreview}
                      disabled={previewing}
                    >
                      {previewing
                        ? <><Spinner size="sm" className="me-2" />Generating preview…</>
                        : <><i className="fas fa-file-pdf me-2"></i>Preview Certificate</>}
                    </Button>
                  </div>
                  {previewing && (
                    <small className="text-muted d-block mt-2">
                      This may take a few seconds…
                    </small>
                  )}
                </Card.Body>
              </Card>
            )}

            {verification && (
              <Card className="shadow-sm mb-4">
                <Card.Header><h6 className="mb-0">QR Verification</h6></Card.Header>
                <Card.Body>
                  <dl className="row mb-0" style={{ fontSize: '0.85rem' }}>
                    <dt className="col-6 text-muted">Status</dt>
                    <dd className="col-6">
                      <Badge bg={verification.isActive && !verification.isExpired ? 'success' : 'danger'}>
                        {verification.isActive && !verification.isExpired ? 'Active' : 'Inactive'}
                      </Badge>
                    </dd>
                    <dt className="col-6 text-muted">Total Scans</dt>
                    <dd className="col-6">{verification.totalScans}</dd>
                  </dl>
                </Card.Body>
              </Card>
            )}

            {submission.trainingClient && (
              <Card className="shadow-sm mb-4">
                <Card.Header><h6 className="mb-0"><i className="fas fa-building me-2"></i>Training Client</h6></Card.Header>
                <Card.Body>
                  <dl className="row mb-0" style={{ fontSize: '0.85rem' }}>
                    <dt className="col-4 text-muted">Client</dt>
                    <dd className="col-8">{submission.trainingClient.client_name}</dd>
                    <dt className="col-4 text-muted">Email</dt>
                    <dd className="col-8">{submission.trainingClient.contact_email}</dd>
                  </dl>
                </Card.Body>
              </Card>
            )}

            {submission.traineePhoto && (
              <Card className="shadow-sm mb-4">
                <Card.Header><h6 className="mb-0">Trainee Photo</h6></Card.Header>
                <Card.Body className="text-center">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt="Trainee"
                      style={{
                        width: '105px',
                        height: '119px',
                        objectFit: 'cover',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px'
                      }}
                    />
                  ) : (
                    <Spinner animation="border" size="sm" variant="secondary" />
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('en-GB');
};

export default ViewTrainingSubmission;
